import { CommonModule } from '@angular/common';

import { AfterViewInit, Component, HostListener, OnDestroy, OnInit, ViewEncapsulation, computed, effect, inject, model, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import {
  AuthPermIdentifier, AuthorizationService, ConfirmationDialogHtmlPaneService, ICurrentUser, NotificationDialogService
  , NotificationPopupService, RequestUserPermission, ResponseUserPermission, UserService
} from '../../../../modules/fwcore';


import { ButtonModule } from '@progress/kendo-angular-buttons';
import { LoaderModule } from '@progress/kendo-angular-indicators';

import { KENDO_SVGICON } from '@progress/kendo-angular-icons';
import { SVGIcon, searchIcon } from '@progress/kendo-svg-icons';


import { AdminService } from '../../../admin';
import { DealsService, HelpContextService } from '../../services';

import { PbCarouselVerticalComponent } from '../../components/home/pb-carousel-vertical/pb-carousel-vertical.component';
import { PbDashboardFrameComponent } from '../../components/home/pb-dashboard-frame/pb-dashboard-frame.component';
import { PbDealUsageComponent } from '../../components/home/pb-deal-usage/pb-deal-usage.component';
import { PbRecentItemsComponent } from '../../components/home/pb-recent-items/pb-recent-items.component';
import { NavigationHelperService } from '../../services';
import { ImpersonationService } from '../../../../modules/fwcore/services/impersonation.service';
import { PbLastVisitSummaryComponent } from '../../components/home/pb-last-visit-summary/pb-last-visit-summary.component';
import { PbNewsComponent } from '../../components/home/pb-news/pb-news.component';
import { UserCompanySummary } from '../../../admin/models';
import { UserCompanyDroplistComponent, ctUserCompanyStorageKey } from '../../../admin/components';

@Component({
    selector: 'app-page-pb-home',
    templateUrl: './page-pb-home.component.html',
    styleUrls: ['./page-pb-home.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule
    , ButtonModule
    , LoaderModule
    , FormsModule
    , ReactiveFormsModule
    , KENDO_SVGICON
    , PbCarouselVerticalComponent
    , PbDashboardFrameComponent
    , PbRecentItemsComponent
    , PbDealUsageComponent
    , PbLastVisitSummaryComponent
    , PbNewsComponent
    , UserCompanyDroplistComponent
  ],
})
export class PagePbHomeComponent implements OnInit, OnDestroy {

  //--------------------------  Constants  ---------------------------------

  protected readonly ctScenariosRoute = '/scenarios/most-recent';

  /** Permission code for the 'share with company' user-group permission. */
  protected readonly ctCodeGroupShare = 'usr-share-cmpy';

  

  //-------------------------  Signal Properties  --------------------------

  /** Page title display text */
  protected heading = signal<string>('Welcome');

  protected heightControlInner = signal<number>(600);

  protected loadingPanelVisible = signal<boolean>(false);


  /** Currently selected user company (two-way model). */
  public userCompany = model<UserCompanySummary | null>(null);

  /** Currently selected company id (two-way model). */
  public currCompanyId = model<string | null>(null);

  /** Whether the 'other companies' selector is visible. */
  protected showCompanyOthers = computed<boolean>(() => this.permCodes().has(this.ctCodeGroupShare));

  //----------------- State properties  ------------------

  protected destroy$ = new Subject

  // ---------------- Api properties ---------------------

  protected user: ICurrentUser;

  //-----------------  UI Search -------------------------

  /** Current search term */
  protected searchTerm = '';

  /** Search icon reference */
  protected searchIcon: SVGIcon = searchIcon;

  /** Flag to search variable names instead of text */
  protected searchVars: boolean = false;

  //-------------------- Permission Properties -------------------------

  /** Permission request payload for the current route. */
  protected reqPerm: RequestUserPermission = {

  }

  /** Permissions returned for the current user/route. */
  protected perms?: AuthPermIdentifier[] = [];

  /** Permission codes resolved for the current user/route. */
  protected permCodes = signal<Set<string>>(new Set<string>());

  /** True once the user-permissions request has completed. */
  protected permsLoaded = false;

  //----------------- Constructor  -----------------------

  /** Publishes this screen's identity for Help to resolve against. */
  private helpContext = inject(HelpContextService);

  constructor(protected router: Router
    , protected authService: AuthorizationService
    , protected adminService: AdminService
    , protected dialogNotification: NotificationDialogService
    , protected dialogConfirmPane: ConfirmationDialogHtmlPaneService
    , protected notification: NotificationPopupService
    , protected dataService: DealsService
    , protected navService: NavigationHelperService
    , protected userService: UserService
    , protected impersonationService: ImpersonationService
  ) {

    this.user = userService.emptyUser;
    this.reqPerm.route = this.router.url;
    // Non-impersonating case is resolved in loadUserPermissions() once showCompanyOthers()
    // confirms this user may use the stored company override - reading it here would race
    // the initial deals fetch with a value left over from a different user on this browser.
    this.currCompanyId.set(
      this.impersonationService.impersonating()
        ? this.impersonationService.companyId()
        : null
    );

    this.setupDebounceEffectHandlers();
  }

  protected setupDebounceEffectHandlers(): void {

    this.setupImpersonationResponse();

  }

  protected setupImpersonationResponse() {

    effect(() => {
      const impersonating = this.impersonationService.impersonating();

      // The company being viewed must not carry over from the admin's own
      // session while impersonating - let the backend resolve it from the
      // impersonated identity instead. Non-impersonating case is resolved in
      // loadUserPermissions() once showCompanyOthers() confirms it's safe to apply.
      this.currCompanyId.set(impersonating
        ? this.impersonationService.companyId()
        : null);

      // update all user based dependents
      this.refresh();
    }, { allowSignalWrites: true });
  }

  

  //-----------------------  Lifecycle Methods  ----------------------------

  /**
   * Initializes component
   */
  ngOnInit(): void {

    // Tell Help where the user is, so the Help menu can open the page about this
    // screen rather than the Help home. 'workspace.home' is the key the Home Page
    // Overview article carries.
    this.helpContext.setKey('workspace.home');
  }


  /**
   * Cleanup on component destruction
   */
  ngOnDestroy() {

    this.destroy$.next();
    this.destroy$.complete();
  }


  //-----------------------  Public API  -----------------------------------

  /**
   * Refreshes screen state based on the current user.
   * Call this after any change that should update the heading or
   * other user-derived UI (e.g. profile edit, alias change, locale switch).
   */
  public refresh(): void {

    this.loadUserPermissions(this.reqPerm);
    this.loadCurrentUser();
  }

  // ------------------- Loader -----------------------------

  protected loaderShow() {
    this.loadingPanelVisible.set(true);
  }

  protected loaderClose() {
    this.loadingPanelVisible.set(false);
  }

  //-----------------------  Event handlers  ----------------------------

  protected onViewAllScenarios(): void {

    this.router.navigate([this.ctScenariosRoute]);
  }

  /** Handles selection of a user-company from the droplist. */
  protected onUserCompanySelected(item: UserCompanySummary | null): void {

    this.userCompany.set(item);
  }

  //-----------------------  UI State Builders  ----------------------------

  protected updateHeading(): void {

    const name = this.buildWelcomeText(this.user);

    this.heading.set(name);
  }

  protected buildWelcomeText(user: ICurrentUser): string {

    const impersonationFirstWord = this.impersonationService.name?.trim().split(/\s+/)[0];

    const rawName = impersonationFirstWord || user.alias || user.firstName || '';
    const displayName = rawName
      ? ' ' + rawName.charAt(0).toUpperCase() + rawName.slice(1)
      : '';

    return 'Welcome' + displayName;
  }

  //-----------------------  Data Loading  ---------------------------------


  /** Fetches user permissions for the current route and applies derived flags. */
  protected loadUserPermissions(reqPerm: RequestUserPermission): void {

    this.authService.getData$<RequestUserPermission, ResponseUserPermission>(reqPerm, this.authService.endpointUserPermission)
      .pipe(takeUntil(this.destroy$))
      .subscribe((resp) => {

        this.perms = resp.data ?? [];

        this.permCodes.set(new Set(this.perms.map(p => p.code).filter((c): c is string => !!c)));

        if (this.impersonationService.impersonating()) {
          this.currCompanyId.set(this.impersonationService.companyId());
        } else if (this.showCompanyOthers()) {
          this.currCompanyId.set(localStorage.getItem(ctUserCompanyStorageKey));
        } else {
          this.currCompanyId.set(null);
        }

        this.permsLoaded = true;
      });
  }

  protected loadCurrentUser(): void {

    this.userService.getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: ICurrentUser) => {

        this.user = user;
        this.updateHeading();
      });
  }


}
